import { ApiError } from '~/lib/api-client';

/**
 * FRONT-2944: a publish the server held because its checks found something
 * new (a possible secret, a new outside site, wider access). The owner gets
 * `confirm` back; anyone else gets the findings and no way to confirm.
 */
export interface HeldPublish {
  message: string;
  sentences: string[];
  confirm?: string;
}

export function heldPublishFrom(err: unknown): HeldPublish | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { code?: unknown; error?: unknown; findings?: unknown; confirm?: unknown } | undefined;
  if (body?.code !== 'needs_confirmation') return null;
  const findings = Array.isArray(body.findings) ? (body.findings as Array<{ sentence?: unknown }>) : [];
  return {
    message: typeof body.error === 'string' ? body.error : err.message,
    sentences: findings.map((f) => f.sentence).filter((s): s is string => typeof s === 'string'),
    ...(typeof body.confirm === 'string' ? { confirm: body.confirm } : {}),
  };
}

type Confirm = (options: { title: string; description?: string; confirmText?: string; cancelText?: string }) => Promise<boolean>;

/**
 * Runs `publish`; when it is held and this person may confirm, shows what was
 * found and, on "Publish anyway", runs it again with the confirmation. Null
 * when the owner chose not to; a hold they cannot confirm is thrown as is.
 */
export async function publishOrConfirm<T>(publish: (confirm?: string) => Promise<T>, confirm: Confirm): Promise<T | null> {
  try {
    return await publish();
  } catch (err) {
    const held = heldPublishFrom(err);
    if (!held?.confirm) throw err;
    const yes = await confirm({
      title: 'Publish with these findings?',
      description: `${held.sentences.join(' ')} Nothing has been published yet.`,
      confirmText: 'Publish anyway',
      cancelText: 'Not now',
    });
    return yes ? publish(held.confirm) : null;
  }
}
